import { ReactElement } from 'react';

import { EvFlexColumn, EvTableColumn } from '@evinced-private/ui-common';

import {
	MOBILE_SDK_TEST_SCAN_CREATED_AT_DAID,
	MOBILE_SDK_TEST_SCAN_NAME_LINK_DAID
} from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import { IMobileScansListRow } from 'src/apps/mobile-common/services/mobile-scans-list';
import { EvLinkLocal } from 'src/common/components/ev-link-local/EvLinkLocal';
import { stringCellFormatter } from 'src/common/components/ev-table/cell-formatters/string-cell-formatter/stringCellFormatter';
import { formatShortMonthDatetime } from 'src/common/helpers/DateFormatHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { SessionViews } from 'src/common/types/WfaSessionViews';

import { MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS } from './MobileSdkTestScans.constants';
import { MOBILE_SDK_TEST_SCANS_LABELS } from './MobileSdkTestScans.labels';
import { MobileSdkTestScanClickHandler } from './MobileSdkTestScans.types';

import './MobileSdkTestScans.scss';

const TOP_ALIGNED_CELL_STYLE = {
	alignItems: 'flex-start',
	verticalAlign: 'top'
};

const metricCellFormatter = (value: number): ReactElement => stringCellFormatter(String(value));

const screenshotCellFormatter = (
	screenshotUrl: string,
	scan: IMobileScansListRow
): ReactElement => {
	if (!screenshotUrl) {
		return (
			<span className="mobile-sdk-test-scans__screenshot-placeholder">
				{MOBILE_SDK_TEST_SCANS_LABELS.NO_SCREENSHOT}
			</span>
		);
	}

	return (
		<img
			src={screenshotUrl}
			alt={MOBILE_SDK_TEST_SCANS_LABELS.SCREENSHOT_ALT(scan.scanName)}
			className="mobile-sdk-test-scans__screenshot"
		/>
	);
};

const scanNameCellFormatter = (
	scanName: string,
	scan: IMobileScansListRow,
	onScanNameClick: MobileSdkTestScanClickHandler,
	currentPath: string
): ReactElement => {
	const scanCreatedAt = scan.scanCreatedAt ? formatShortMonthDatetime(scan.scanCreatedAt) : '';
	const handleClick = (): void => onScanNameClick(scan);

	return (
		<EvFlexColumn align="START" gap="NONE">
			<EvLinkLocal
				ariaLabel={MOBILE_SDK_TEST_SCANS_LABELS.SCAN_NAME_LINK_ARIA_LABEL(scanName)}
				className="mobile-sdk-test-scans__scan-name-link"
				dataAutomationId={MOBILE_SDK_TEST_SCAN_NAME_LINK_DAID}
				id={scan.scanId}
				isDark
				linkText={scanName}
				onClick={handleClick}
				state={{ from: currentPath }}
				url={RoutesHelper.getMobileSdkTestPath(scan.runId, SessionViews.ALL_ISSUES)}
			/>
			{scanCreatedAt && (
				<div
					className="mobile-sdk-test-scans__scan-created-at"
					data-automation-id={MOBILE_SDK_TEST_SCAN_CREATED_AT_DAID}
				>
					{scanCreatedAt}
				</div>
			)}
		</EvFlexColumn>
	);
};

export const getMobileSdkTestScansColumns = (
	onScanNameClick: MobileSdkTestScanClickHandler,
	currentPath: string
): EvTableColumn[] => [
	{
		dataField: 'scanScreenshotUrl',
		text: MOBILE_SDK_TEST_SCANS_LABELS.SCREENSHOT,
		style: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.SCREENSHOT, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.SCREENSHOT },
		formatter: screenshotCellFormatter
	},
	{
		dataField: 'scanName',
		text: MOBILE_SDK_TEST_SCANS_LABELS.SCAN_NAME,
		style: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.SCAN_NAME, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.SCAN_NAME },
		sort: true,
		formatter: (scanName: string, scan: IMobileScansListRow) =>
			scanNameCellFormatter(scanName, scan, onScanNameClick, currentPath)
	},
	{
		dataField: 'totalIssues',
		text: MOBILE_SDK_TEST_SCANS_LABELS.TOTAL_ISSUES,
		style: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.METRIC, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.METRIC },
		sort: true,
		formatter: metricCellFormatter
	},
	{
		dataField: 'criticalIssues',
		text: MOBILE_SDK_TEST_SCANS_LABELS.CRITICAL,
		style: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.METRIC, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.METRIC },
		sort: true,
		formatter: metricCellFormatter
	},
	{
		dataField: 'seriousIssues',
		text: MOBILE_SDK_TEST_SCANS_LABELS.SERIOUS,
		style: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.METRIC, ...TOP_ALIGNED_CELL_STYLE },
		headerStyle: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.METRIC },
		sort: true,
		formatter: metricCellFormatter
	},
	{
		dataField: 'elementsWithIssues',
		text: MOBILE_SDK_TEST_SCANS_LABELS.ELEMENTS_WITH_ISSUES,
		style: {
			width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.ELEMENTS_WITH_ISSUES,
			...TOP_ALIGNED_CELL_STYLE
		},
		headerStyle: { width: MOBILE_SDK_TEST_SCANS_COLUMN_WIDTHS.ELEMENTS_WITH_ISSUES },
		sort: true,
		formatter: metricCellFormatter
	}
];
